// Qué partes de un texto se RESALTAN en la caja: los colores `#RRGGBB` / `#RGB` y las
// menciones `@Nombre` de los adjuntos enganchados
// (ADR-2026-10-04-colores-y-menciones-resaltados-en-la-caja). Puro: devuelve el texto
// partido en segmentos, en orden y sin perder un carácter, para que la capa espejo detrás
// del textarea pinte cada uno. Los índices son del texto ORIGINAL (nada se normaliza
// cambiando largos), porque la capa tiene que calcar glifo por glifo.

import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import { nombreDeMencion } from '../../pages/v2/mencionesDeAdjuntos';

export type TipoDeSegmento = 'texto' | 'color' | 'mencion';

export interface Segmento {
  tipo: TipoDeSegmento;
  texto: string;
}

export interface Tramo {
  desde: number;
  hasta: number;
  tipo: Exclude<TipoDeSegmento, 'texto'>;
  /** En una mención: el id del adjunto mencionado (para traducirla a `@ImageN` al mandar). */
  adjuntoId?: string;
}

/** El mismo patrón que `hexesEn`, pero mirando DÓNDE está cada color. */
const COLOR = /(^|[^\p{L}\p{N}#])#([0-9a-f]{6}|[0-9a-f]{3})(?![\p{L}\p{N}])/giu;

/**
 * Cada carácter a su base en minúscula, UNO por uno (no `normalize` sobre el texto entero,
 * que cambia el largo con los acentos): así una comparación sin acentos ni mayúsculas
 * conserva los índices del original.
 */
function base(texto: string): string {
  let out = '';
  for (const c of texto) {
    const b = c.normalize('NFD')[0] ?? c;
    const m = b.toLowerCase();
    // `toLowerCase` puede devolver más de un carácter en casos raros: se toma uno solo
    // para no mover los índices; y un par sustituto (emoji) se copia tal cual.
    out += c.length === 2 ? c : m.length === 1 ? m : b;
  }
  return out;
}

const esDePalabra = (c: string | undefined): boolean => !!c && /[\p{L}\p{N}]/u.test(c);
const puedeAbrirMencion = (c: string | undefined): boolean => c === undefined || /[\s(]/u.test(c);

/** Los tramos de color y de mención, sin solaparse, ordenados por posición. */
export function tramosResaltados(texto: string, adjuntos: readonly ChatAttachment[]): Tramo[] {
  const tramos: Tramo[] = [];
  for (const m of texto.matchAll(COLOR)) {
    const desde = (m.index ?? 0) + m[1].length;
    tramos.push({ desde, hasta: desde + 1 + m[2].length, tipo: 'color' });
  }
  if (adjuntos.length > 0) {
    const t = base(texto);
    // Los nombres más largos primero: "@Remera oversize" gana sobre "@Remera". Con dos
    // adjuntos del mismo nombre, la mención es del primero.
    const porNombre = new Map<string, string>();
    for (const a of adjuntos) {
      const n = nombreDeMencion(a, adjuntos);
      if (n && !porNombre.has(n)) porNombre.set(n, a.id);
    }
    const nombres = [...porNombre.keys()].sort((a, b) => b.length - a.length);
    for (const nombre of nombres) {
      const aguja = `@${base(nombre)}`;
      let i = t.indexOf(aguja);
      while (i !== -1) {
        const hasta = i + aguja.length;
        const libre = !tramos.some((x) => i < x.hasta && hasta > x.desde);
        if (libre && puedeAbrirMencion(t[i - 1]) && !esDePalabra(t[hasta])) {
          tramos.push({ desde: i, hasta, tipo: 'mencion', adjuntoId: porNombre.get(nombre) });
        }
        i = t.indexOf(aguja, i + 1);
      }
    }
  }
  return tramos.sort((a, b) => a.desde - b.desde);
}

/** El texto entero partido en segmentos; concatenados devuelven el original. */
export function segmentosDeTexto(texto: string, adjuntos: readonly ChatAttachment[]): Segmento[] {
  const out: Segmento[] = [];
  let pos = 0;
  for (const tr of tramosResaltados(texto, adjuntos)) {
    if (tr.desde > pos) out.push({ tipo: 'texto', texto: texto.slice(pos, tr.desde) });
    out.push({ tipo: tr.tipo, texto: texto.slice(tr.desde, tr.hasta) });
    pos = tr.hasta;
  }
  if (pos < texto.length) out.push({ tipo: 'texto', texto: texto.slice(pos) });
  return out;
}
